import { Access } from "../../access/models/Access";
import { SharingStatus } from "../../access/models/SharingStatus";
import { Page } from "../../shared/utils/http-utils";
import {
  jwtAuthAxios,
  optionalAuthAxios,
} from "../../shared/utils/network-utils";
import { DashboardTypes } from "../models/Dashboard";
import { CraForecastScenario } from "../models/CraForecastScenario";
import {
  CreateSurvey,
  Survey,
  UpdateSharingStatus,
  UpdateSurvey,
  SurveyDefaults,
  UpdateSurveyDefaults,
} from "../models/Survey";
import { HlaForecastScenario } from "../models/HlaForecastScenario";
import { OptionalScope } from "../../shared/models/OptionalScope";
import { CraWay } from "../../cra/models/CraWay";
import { QueryPolygon } from "../../hla/models/QueryPolygon";
import { HlaWay } from "../../hla/models/HlaWay";

export const SurveyApi = {
  validatePassword: async (surveyId: string) =>
    await optionalAuthAxios().get(`/surveys/${surveyId}/validate-password`),

  get: async (surveyId: string) =>
    await optionalAuthAxios().get<Survey>(`/surveys/${surveyId}`),

  page: async (pageSize: number, pageNumber: number) =>
    await jwtAuthAxios().get<Page<Survey>>(`/surveys`, {
      params: { "page[size]": pageSize, "page[number]": pageNumber },
    }),

  create: async (survey: CreateSurvey) =>
    await jwtAuthAxios().post<Survey>(`/surveys`, survey),

  update: async (body: UpdateSurvey, id: string) =>
    await jwtAuthAxios().put<Survey>(`/surveys/${id}`, body),

  delete: async (surveyId: string) =>
    await jwtAuthAxios().delete(`/surveys/${surveyId}`),

  deleteMyAccess: async (surveyId: string) =>
    await jwtAuthAxios().delete(`/surveys/${surveyId}/my-access`),

  getSharingStatus: async (surveyId: string) =>
    await jwtAuthAxios().get<SharingStatus>(
      `/surveys/${surveyId}/sharing-status`,
    ),

  updateSharingStatus: async (surveyId: string, body: UpdateSharingStatus) =>
    await jwtAuthAxios().put<SharingStatus>(
      `/surveys/${surveyId}/sharing-status`,
      body,
    ),

  listAccesses: async (surveyId: string) =>
    await jwtAuthAxios().get<Access[]>(`/surveys/${surveyId}/accesses`),

  postAccountAccess: async (surveyId: string) =>
    await jwtAuthAxios().post<Survey>(`/surveys/${surveyId}/account-access`),

  listDashboards: async (surveyId: string) =>
    await optionalAuthAxios().get<DashboardTypes.Dashboard[]>(
      `/surveys/${surveyId}/dashboards`,
    ),

  listCraForecastScenarios: async (surveyId: string) =>
    await optionalAuthAxios().get<CraForecastScenario[]>(
      `/surveys/${surveyId}/cra-forecast-scenarios`,
    ),

  listHlaForecastScenarios: async (surveyId: string) =>
    await optionalAuthAxios().get<HlaForecastScenario[]>(
      `/surveys/${surveyId}/hla-forecast-scenarios`,
    ),

  getDefaults: async (surveyId: string) =>
    await optionalAuthAxios().get<SurveyDefaults>(
      `/surveys/${surveyId}/defaults`,
    ),

  updateDefaults: async (surveyId: string, body: UpdateSurveyDefaults) =>
    await jwtAuthAxios().put<SurveyDefaults>(
      `/surveys/${surveyId}/defaults`,
      body,
    ),

  refreshHlaScores: async (surveyId: string) =>
    await jwtAuthAxios().post<string>(
      `/surveys/${surveyId}/refresh-hla-scores`,
    ),

  pageCraWays: async (
    surveyId: string,
    scopes: OptionalScope[] = [],
    pageSize: number,
    pageNumber: number,
  ) =>
    await optionalAuthAxios().get<Page<CraWay>>(
      `/surveys/${surveyId}/cra-ways`,
      {
        params: {
          "page[size]": pageSize,
          "page[number]": pageNumber,
          scopes: scopes.join(),
        },
      },
    ),

  pageHlaWays: async (surveyId: string, pageSize: number, pageNumber: number) =>
    await optionalAuthAxios().get<Page<HlaWay>>(
      `/surveys/${surveyId}/hla-ways`,
      {
        params: {
          "page[size]": pageSize,
          "page[number]": pageNumber,
        },
      },
    ),

  queryPolygon: async (id: string, body: QueryPolygon) =>
    await jwtAuthAxios().post<string>(`/surveys/${id}/query-polygon`, body),
};
